import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PaginationInterfaceLF, ReportInterace } from "app/types";
import {
  AddPopupDetails,
  DefaultDiscount,
  DefaultDiscountPayload,
  PostAddPopupAPIpayload,
  PromotionLogs,
  Promotions,
  PromotionsPayload,
} from "app/types/promotions";
import { AppDispatch } from "../store";
import { RootState } from "../reducers/hooks";
import { errorHandler } from "app/shared/Service/errorHandler";
import { alertMessage } from "../actions/common";
import { createQuery, getActiveConfig } from "app/utils/constants/common";
import {
  deleteDefaultDiscountApi,
  downloadPromotionsReportApi,
  getAddPopupAPI,
  getDefaultDiscountsApi,
  getPromotionLogsApi,
  getPromotionsByIdApi,
  getPromotionsListApi,
  PostAddPopupAPI,
  postDefaultDiscountApi,
  postPromotionsApi,
  PutAddPopupAPI,
  putDefaultDiscountApi,
  putPromotionsApi,
} from "app/services/promotions";
import qs from "qs";

interface PromotionsFilters {
  storeId: number;
  search: string;
  status: string;
  showToCheckout: string;
}
interface PopupFilters {
  title?: string;
  content?: string;
  displayTypes?: string[];
  statuses?: string[];
}
interface InitialState {
  promotionsList: Promotions[];
  promotionsPagination: PaginationInterfaceLF;
  promotionsListRowPerPage: number;
  filters: PromotionsFilters | null;
  isSubmitting: boolean;
  isPromotionListLoading: boolean;

  defaultDiscountList: DefaultDiscount[];
  defaultDiscountPagination: PaginationInterfaceLF;
  defaultDiscountListRowPerPage: number;
  isDefaultDiscountLoading: boolean;
  isSubmittingDefaultDiscount: boolean;

  promotionLogs: PromotionLogs[];
  promotionLogsPagination: PaginationInterfaceLF;
  promotionLogsListRowPerPage: number;
  isPromotionLogsLoading: boolean;
  isPromotionReportsLoading: boolean;

  addPopupsData: AddPopupDetails[];
  isAdvertisementPopupSubmitting: boolean;
  popupFilters: PopupFilters | null;
}

const initialState: InitialState = {
  promotionsList: [],
  promotionsPagination: null,
  promotionsListRowPerPage: 20,
  filters: null,
  isSubmitting: false,
  isPromotionListLoading: false,
  defaultDiscountList: [],
  defaultDiscountPagination: null,
  defaultDiscountListRowPerPage: 20,
  isDefaultDiscountLoading: false,
  isSubmittingDefaultDiscount: false,
  promotionLogs: [],
  promotionLogsPagination: null,
  promotionLogsListRowPerPage: 20,
  isPromotionLogsLoading: false,
  isPromotionReportsLoading: false,
  addPopupsData: [],
  isAdvertisementPopupSubmitting: false,
  popupFilters: null,
};

export const promotionsSlice = createSlice({
  name: "promotions",
  initialState,
  reducers: {
    setPromotionsList: (
      state,
      action: PayloadAction<{
        promotions: Promotions[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.promotionsList = action.payload.promotions;
      state.promotionsPagination = action.payload.pagination;
    },
    setPromotionsListRowPerPage: (state, action: PayloadAction<number>) => {
      state.promotionsListRowPerPage = action.payload;
    },
    setIsSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },
    updatePromotionById: (state, action: PayloadAction<Promotions>) => {
      const index = state.promotionsList.findIndex(
        (p: Promotions) => p.id === action.payload.id,
      );
      state.promotionsList[index] = action.payload;
    },
    setDefaultDiscountList: (
      state,
      action: PayloadAction<{
        defaultDiscountList: DefaultDiscount[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.defaultDiscountList = action.payload.defaultDiscountList;
      state.defaultDiscountPagination = action.payload.pagination;
    },
    setDefaultDiscountListRowPerPage: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.defaultDiscountListRowPerPage = action.payload;
    },
    setIsDefaultDiscountLoading: (state, action: PayloadAction<boolean>) => {
      state.isDefaultDiscountLoading = action.payload;
    },
    setPromotionFilters: (
      state,
      action: PayloadAction<PromotionsFilters | null>,
    ) => {
      state.filters = action.payload;
    },
    setIsPromotionListLoading: (state, action: PayloadAction<boolean>) => {
      state.isPromotionListLoading = action.payload;
    },
    setIsSubmittingDefaultDiscount: (state, action: PayloadAction<boolean>) => {
      state.isSubmittingDefaultDiscount = action.payload;
    },
    setPromotionLogs: (
      state,
      action: PayloadAction<{
        promotionLogs: PromotionLogs[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.promotionLogs = action.payload.promotionLogs;
      state.promotionLogsPagination = action.payload.pagination;
    },
    setPromotionLogsListRowPerPage: (state, action: PayloadAction<number>) => {
      state.promotionLogsListRowPerPage = action.payload;
    },
    setIsPromotionLogsLoading: (state, action: PayloadAction<boolean>) => {
      state.isPromotionLogsLoading = action.payload;
    },
    setIsPromotionReportsLoading: (state, action: PayloadAction<boolean>) => {
      state.isPromotionReportsLoading = action.payload;
    },
    setAddPopupsData: (state, action: PayloadAction<AddPopupDetails[]>) => {
      state.addPopupsData = action.payload;
    },
    setIsAdvertisementPopupSubmitting: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isAdvertisementPopupSubmitting = action.payload;
    },
    setPopupFilters: (state, action: PayloadAction<PopupFilters | null>) => {
      state.popupFilters = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setPromotionsList,
  setPromotionsListRowPerPage,
  setIsSubmitting,
  updatePromotionById,
  setDefaultDiscountList,
  setDefaultDiscountListRowPerPage,
  setIsDefaultDiscountLoading,
  setPromotionFilters,
  setIsPromotionListLoading,
  setIsSubmittingDefaultDiscount,
  setPromotionLogs,
  setPromotionLogsListRowPerPage,
  setIsPromotionLogsLoading,
  setIsPromotionReportsLoading,
  setAddPopupsData,
  setIsAdvertisementPopupSubmitting,
  setPopupFilters,
} = promotionsSlice.actions;
export default promotionsSlice.reducer;

type GetPromotionsListAction = {
  continutationToken?: string;
};
export const getPromotionsListAction = ({
  continutationToken = null,
}: GetPromotionsListAction) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { storeId } = getActiveConfig();
      const { filters, promotionsListRowPerPage } = getState().promotions;
      const customFields = [
        {
          customKey: "fromDate",
          newValue: "validFrom",
          valueType: "text" as const,
        },
        {
          customKey: "toDate",
          newValue: "validUntil",
          valueType: "text" as const,
        },
      ];

      let query = "";
      query =
        "?" +
        createQuery({
          filter: {
            ...filters,
            ...(filters?.showToCheckout === "Active"
              ? { showToCheckout: true }
              : { showToCheckout: false }),
            limit: promotionsListRowPerPage,
            continutationToken,
          },
          customFields,
        });

      dispatch(setIsPromotionListLoading(true));
      const { data } = await getPromotionsListApi(storeId, query);
      dispatch(setIsPromotionListLoading(false));

      const pagination = data.pagination;
      const promotions = data.data;

      dispatch(setPromotionsList({ promotions, pagination }));
    } catch (error) {
      dispatch(setIsPromotionListLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const postPromotionsAction = (payload: PromotionsPayload) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setIsSubmitting(true));
      const { data } = await postPromotionsApi({
        payload,
        storeId,
      });
      dispatch(setIsSubmitting(false));
      dispatch(alertMessage("Promotion added successfully", "success"));
      return data;
    } catch (error) {
      dispatch(setIsSubmitting(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const putPromotionsAction = ({
  promotionId,
  payload,
}: {
  promotionId: number;
  payload: PromotionsPayload;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setIsSubmitting(true));
      const { data } = await putPromotionsApi({
        payload,
        storeId,
        promotionId: promotionId,
      });
      dispatch(setIsSubmitting(false));
      dispatch(alertMessage("Promotion updated successfully", "success"));
      return data?.data;
    } catch (error) {
      dispatch(setIsSubmitting(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getPromotionByIdAction = (promotionId: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setIsSubmitting(true));
      const { data } = await getPromotionsByIdApi({
        storeId,
        promotionId,
      });
      dispatch(setIsSubmitting(false));
      return data;
    } catch (error) {
      dispatch(setIsSubmitting(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getDefaultDiscountListAction = ({
  promotionId,
  continutationToken = null,
}: {
  promotionId: number;
  continutationToken?: string;
}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { storeId } = getActiveConfig();
      const { defaultDiscountListRowPerPage } = getState().promotions;

      const query =
        "?" +
        createQuery({
          filter: {
            limit: defaultDiscountListRowPerPage,
            continutationToken,
          },
        });
      dispatch(setIsDefaultDiscountLoading(true));
      const { data } = await getDefaultDiscountsApi({
        storeId,
        promotionId,
        query,
      });
      dispatch(setIsDefaultDiscountLoading(false));

      const pagination = data.pagination;
      const defaultDiscounts = data.data;
      // const users = data.included?.users || [];

      dispatch(
        setDefaultDiscountList({
          defaultDiscountList: defaultDiscounts,
          pagination,
        }),
      );
    } catch (error) {
      dispatch(setIsDefaultDiscountLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

type PostDefaultDiscountAction = {
  promotionId: number;
  payload: DefaultDiscountPayload;
};

export const postDefaultDiscountAction = ({
  promotionId,
  payload,
}: PostDefaultDiscountAction) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setIsSubmittingDefaultDiscount(true));
      const { data } = await postDefaultDiscountApi({
        payload,
        storeId,
        promotionId,
      });
      dispatch(setIsSubmittingDefaultDiscount(false));
      dispatch(alertMessage("Default discount added successfully", "success"));
      return data;
    } catch (error) {
      dispatch(setIsSubmittingDefaultDiscount(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const putDefaultDiscountAction = ({
  promotionId,
  defaultDiscountId,
  payload,
}: {
  promotionId: number;
  defaultDiscountId: number;
  payload: DefaultDiscountPayload;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setIsSubmittingDefaultDiscount(true));
      const { data } = await putDefaultDiscountApi({
        payload,
        storeId,
        promotionId,
        defaultDiscountId,
      });
      dispatch(setIsSubmittingDefaultDiscount(false));
      dispatch(
        alertMessage("Default discount updated successfully", "success"),
      );
      return data;
    } catch (error) {
      dispatch(setIsSubmittingDefaultDiscount(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const deleteDefaultDiscountAction = ({
  promotionId,
  defaultDiscountId,
}: {
  promotionId: number;
  defaultDiscountId: number;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      const response = await deleteDefaultDiscountApi({
        storeId,
        promotionId,
        defaultDiscountId,
      });
      dispatch(
        alertMessage("Default discount deleted successfully", "success"),
      );
      return response;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getPromotionLogsAction = ({
  promotionId,
  continutationToken = null,
}: {
  promotionId: number;
  continutationToken?: string;
}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { storeId } = getActiveConfig();
      const { promotionLogsListRowPerPage } = getState().promotions;

      const query =
        "?" +
        createQuery({
          filter: {
            limit: promotionLogsListRowPerPage,
            continutationToken,
          },
        });
      dispatch(setIsPromotionLogsLoading(true));
      const { data } = await getPromotionLogsApi({
        storeId,
        promotionId,
        query,
      });
      dispatch(setIsPromotionLogsLoading(false));

      const pagination = data.pagination;
      const promotionLogs = data.data;
      const users = data.included?.users || [];

      // loop through promotionLogs and add user details
      promotionLogs.forEach((log: PromotionLogs) => {
        const user = users.find((u) => u.id === log.userId);
        log.user = user;
      });

      dispatch(
        setPromotionLogs({
          promotionLogs,
          pagination,
        }),
      );
    } catch (error) {
      dispatch(setIsPromotionLogsLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
export interface PromotionsReportPayload {
  ValidFrom: string;
  ValidUntil: string;
  PromoTypes: string[];
  Categories: string[];
  Statuses: string[];
}

export const downloadPromotionsReportAction = (
  payload: PromotionsReportPayload,
) => {
  return async (dispatch: AppDispatch): Promise<ReportInterace> => {
    try {
      const { storeId } = getActiveConfig();

      const query = "?" + qs.stringify(payload, { arrayFormat: "repeat" });
      dispatch(setIsPromotionReportsLoading(true));
      const {
        data: { data },
      } = await downloadPromotionsReportApi(storeId, query);
      dispatch(setIsPromotionReportsLoading(false));
      return data;
    } catch (error) {
      dispatch(setIsPromotionReportsLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getAddPopupsAction = () => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { storeId } = getActiveConfig();
      const { popupFilters } = getState().promotions;

      const cleanedFilters = Object.fromEntries(
        Object.entries(popupFilters || {}).filter(
          ([_, value]) =>
            value !== null &&
            value !== undefined &&
            value !== "" &&
            !(Array.isArray(value) && value.length === 0),
        ),
      );

      const query =
        Object.keys(cleanedFilters).length > 0
          ? "?" + qs.stringify(cleanedFilters, { arrayFormat: "repeat" })
          : "";

      const { data } = await getAddPopupAPI(storeId, query);

      dispatch(setAddPopupsData(data.data));
    } catch (error) {
      dispatch(setIsPromotionReportsLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const postAddPopupAction = (
  payload: PostAddPopupAPIpayload,
  onSuccess?: () => void,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setIsAdvertisementPopupSubmitting(true));
      const response = await PostAddPopupAPI(storeId, payload);
      if (response?.status === 200) {
        dispatch(setIsAdvertisementPopupSubmitting(false));
        dispatch(alertMessage("Popup added successfully", "success"));
        onSuccess?.();
        return response.data;
      } else {
        dispatch(setIsAdvertisementPopupSubmitting(false));
        const message = "Unexpected error occurred";
        dispatch(alertMessage(message, "error"));
        throw new Error(message);
      }
    } catch (error) {
      dispatch(setIsAdvertisementPopupSubmitting(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const putAddPopupAction = (
  popupId: number,
  payload: PostAddPopupAPIpayload,
  onSuccess?: () => void,
) => {
  return async (dispatch: AppDispatch): Promise<any> => {
    try {
      const { storeId } = getActiveConfig();

      dispatch(setIsAdvertisementPopupSubmitting(true));
      const response = await PutAddPopupAPI(popupId, storeId, payload);

      if (response?.status === 200) {
        dispatch(setIsAdvertisementPopupSubmitting(false));
        dispatch(alertMessage("Popup updated successfully", "success"));
        onSuccess?.();
        return response.data;
      } else {
        dispatch(setIsAdvertisementPopupSubmitting(false));
        const message = "Unexpected error occurred";
        dispatch(alertMessage(message, "error"));
        throw new Error(message);
      }
    } catch (error: unknown) {
      dispatch(setIsAdvertisementPopupSubmitting(false));
      dispatch(alertMessage(error, "error"));
      throw error;
    } finally {
      dispatch(setIsAdvertisementPopupSubmitting(false));
    }
  };
};
